import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const BASE_URL = "https://www.pointie.ca";
const SITE_NAME = "Let's Pointie";
const DEFAULT_IMAGE = `${BASE_URL}/pointie_logo.png`;

const OG_LOCALE_MAP = {
  en: "en_CA",
  fr: "fr_CA",
  zh: "zh_CN",
  "zh-TW": "zh_TW",
  pa: "pa_IN",
  es: "es_ES",
  tl: "tl_PH",
  ar: "ar_SA",
  ko: "ko_KR",
  ja: "ja_JP",
};

const NON_EN_LANGS = ["fr", "zh", "zh-TW", "pa", "es", "tl", "ar", "ko", "ja"];

export default function SEO({ title, description, path = "/", noIndex = false, breadcrumbs }) {
  const { i18n, t } = useTranslation("nav");
  const lang = i18n.language;
  const ogLocale = OG_LOCALE_MAP[lang] || "en_CA";
  const prefix = NON_EN_LANGS.includes(lang) ? `/${lang}` : "";

  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Compare Cashback & Points in Canada`;
  const url = `${BASE_URL}${prefix}${path}`;

  const breadcrumbJsonLd =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            const entry = {
              "@type": "ListItem",
              position: idx + 1,
              name: t(crumb.name),
            };
            if (!isLast) {
              entry.item = `${BASE_URL}${prefix}${crumb.path}`;
            }
            return entry;
          }),
        }
      : null;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={DEFAULT_IMAGE} />

      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* Blog feed auto-discovery (RSS 2.0 + Atom 1.0) — swaps to locale feed
          on client-rendered routes so the head matches the static-HTML one
          emitted by postbuild.js for the same locale. */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${SITE_NAME} Blog — RSS`}
        href={`${BASE_URL}${prefix}/feed.xml`}
      />
      <link
        rel="alternate"
        type="application/atom+xml"
        title={`${SITE_NAME} Blog — Atom`}
        href={`${BASE_URL}${prefix}/atom.xml`}
      />

      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}
    </Helmet>
  );
}

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string.isRequired,
  path: PropTypes.string,
  noIndex: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      path: PropTypes.string,
    })
  ),
};
